import { Component, inject, OnDestroy, Type } from '@angular/core';
import { NavigationEnd } from '@angular/router';
import { filter, lastValueFrom, take, tap } from 'rxjs';
import { PrivateLayout } from './private-layout.types';
import { defaultPrivateLayoutConfig, PrivateLayoutConfig } from './private-layout.config';
import { LegacyIntegrationService } from '../../integrations/legacy-integration.service';
import { NotificationIntegrationService } from '../../integrations/notification-integration.service';
import { AuthCheckService, AuthService, SvcDocumentTitleService, UserService } from 'projects/lib-shared-core/src/public-api';
import { SvcGlobalDataPrefetching } from 'projects/lib-shared-core/src/lib/data-prefetching/svc-global-data-prefetching';
import { SvcAppSettings, SvcModule, toggleSplashScreenVisibility } from 'projects/lib-shared-common/src/public-api';
import { BaseLayoutComponent } from '../shared/base-layout.component';
import { PrivateBaseLayoutComponent } from './layouts/private-base-layout.component';

@Component({
  selector: 'svc-private-layout',
  templateUrl: './private-layout.component.html',
  styleUrls: ['./private-layout.component.scss'],
})
export class SvcPrivateLayoutComponent extends BaseLayoutComponent<PrivateLayout, PrivateBaseLayoutComponent> implements OnDestroy {
  private readonly _legacyService = inject(LegacyIntegrationService);
  private readonly _userService = inject(UserService);
  private readonly _notificationIntegrationService = inject(NotificationIntegrationService);
  private readonly _authCheckService = inject(AuthCheckService);
  private readonly _authService = inject(AuthService);
  private readonly _svcGlobalDataPrefetching = inject(SvcGlobalDataPrefetching);
  private readonly _svcAppSettings = inject(SvcAppSettings);
  private readonly _documentTitleService = inject(SvcDocumentTitleService);

  protected get defaultLayoutConfig(): PrivateLayoutConfig {
    return defaultPrivateLayoutConfig;
  }

  constructor() {
    super();
    this._documentTitleService.init();
    this._router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        take(1),
        tap(async () => {
          if (this._startupConfigService.isInitialized || (await lastValueFrom(this._authService.check()))) {
            toggleSplashScreenVisibility();
          }
        })
      )
      .subscribe();
    this._authService.checkAuthenticatedProperty();
  }

  protected getLanguage(): string {
    return this._userService?.user?.languageTag ?? 'en-US';
  }

  public childLayoutWasResolved(): void {
    if ([SvcModule.Feed].includes(this._svcAppSettings.module)) return;
    this._legacyService.addLegacySupport();
    this._notificationIntegrationService.addIntegration();
    this._svcGlobalDataPrefetching.initAndFetch();
    this._authService.syncLegacySessionCookies();
    this._authCheckService.startCheckSessionState();
  }

  protected getLayoutImportPromiseFnByLayout(layout: PrivateLayout): () => Promise<{ component: Type<PrivateBaseLayoutComponent>; module: Type<any> }> {
    switch (layout) {
      case 'svc-custom-layout': {
        return () =>
          import('./layouts/custom/private-custom-layout.component').then((c) => ({
            component: c.PrivateCustomLayoutComponent,
            module: c.PrivateCustomLayoutModule,
          }));
      }
      case 'svc-workspace-layout': {
        return () =>
          import('./layouts/workspace/private-workspace-layout.component').then((c) => ({
            component: c.PrivateWorkspaceLayoutComponent,
            module: c.PrivateWorkspaceLayoutModule,
          }));
      }
      default: {
        return () =>
          import('./layouts/empty/private-empty-layout.component').then((c) => ({
            component: c.PrivateEmptyLayoutComponent,
            module: c.PrivateEmptyLayoutModule,
          }));
      }
    }
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    this._authCheckService.stopCheckSessionState();
  }
}
